/**
 * Matriz TPL — agrupamento e métricas do Projeto do Trabalho Padrão.
 * Interpretação frontend; não altera regras de negócio.
 */

import { buildPlannerBlocks } from './tplPlannerBlocks';
import { templateHasLinkedIndicator } from '../utils/lstIndicator';
import type { LstTemplate } from '../types';

export type TplMatrixGroupKey =
  | 'inicio_turno'
  | 'rotina_operacional'
  | 'gestao_diaria'
  | 'escalonamento'
  | 'melhoria'
  | 'resultados';

export const TPL_MATRIX_GROUP_LABELS: Record<TplMatrixGroupKey, string> = {
  inicio_turno: 'Início do turno',
  rotina_operacional: 'Rotina operacional',
  gestao_diaria: 'Gestão diária',
  escalonamento: 'Escalonamento',
  melhoria: 'Melhoria',
  resultados: 'Resultados',
};

export const TPL_MATRIX_GROUP_ORDER: TplMatrixGroupKey[] = [
  'inicio_turno',
  'rotina_operacional',
  'gestao_diaria',
  'escalonamento',
  'melhoria',
  'resultados',
];

export interface TplMatrixGroupStat {
  key: TplMatrixGroupKey;
  label: string;
  count: number;
  minutesWeek: number;
  pctAgenda: number;
  templates: LstTemplate[];
}

export interface TplProjectHeaderStats {
  totalRotinas: number;
  minutesWeek: number;
  criticalCount: number;
  indicatorLinkedCount: number;
  withoutIndicatorCount: number;
  withoutCategoryCount: number;
  conflictingCount: number;
}

export interface TplProjectMatrixData {
  header: TplProjectHeaderStats;
  groups: TplMatrixGroupStat[];
  highlightIds: Set<string>;
  conflictingIds: Set<string>;
}

function weeklyMinutes(template: LstTemplate): number {
  const d = template.duration_minutes ?? 15;
  if (template.frequency === 'horaria' || template.frequency === 'diaria') return d * 5;
  if (template.frequency === 'semanal') return d;
  return d;
}

export function resolveTplMatrixGroup(template: LstTemplate): TplMatrixGroupKey {
  if (template.activity_kind === 'reuniao_camada') return 'escalonamento';
  if (templateHasLinkedIndicator(template)) return 'resultados';
  if (template.period === 'inicio') return 'inicio_turno';
  if (template.period === 'fim') return 'melhoria';
  if (template.category === 'producao') return 'rotina_operacional';
  return 'gestao_diaria';
}

function countConflictingTemplates(templates: LstTemplate[], weekDates: string[]): Set<string> {
  const blocks = buildPlannerBlocks(templates.filter((t) => t.active), weekDates);
  const byCell = new Map<string, string[]>();
  for (const b of blocks) {
    const key = `${b.day}-${b.hour}`;
    const arr = byCell.get(key) ?? [];
    arr.push(b.templateId);
    byCell.set(key, arr);
  }
  const conflicting = new Set<string>();
  for (const ids of byCell.values()) {
    if (ids.length <= 1) continue;
    for (const id of ids) conflicting.add(id);
  }
  return conflicting;
}

export function computeTplProjectMatrix(
  templates: LstTemplate[],
  weekDates: string[] = [],
): TplProjectMatrixData {
  const active = templates.filter((t) => t.active);
  const totalMinutes = active.reduce((s, t) => s + weeklyMinutes(t), 0);
  const conflictingIds = weekDates.length > 0 ? countConflictingTemplates(active, weekDates) : new Set<string>();

  const highlightIds = new Set<string>();
  for (const t of active) {
    if (t.is_critical) highlightIds.add(t.id);
    if (!templateHasLinkedIndicator(t)) highlightIds.add(t.id);
    if (!t.category) highlightIds.add(t.id);
    if (conflictingIds.has(t.id)) highlightIds.add(t.id);
  }

  const byGroup = new Map<TplMatrixGroupKey, LstTemplate[]>();
  for (const key of TPL_MATRIX_GROUP_ORDER) byGroup.set(key, []);
  for (const t of active) {
    byGroup.get(resolveTplMatrixGroup(t))!.push(t);
  }

  const groups: TplMatrixGroupStat[] = TPL_MATRIX_GROUP_ORDER.map((key) => {
    const items = byGroup.get(key) ?? [];
    const minutesWeek = items.reduce((s, t) => s + weeklyMinutes(t), 0);
    return {
      key,
      label: TPL_MATRIX_GROUP_LABELS[key],
      count: items.length,
      minutesWeek,
      pctAgenda: totalMinutes > 0 ? Math.round((minutesWeek / totalMinutes) * 100) : 0,
      templates: items.sort((a, b) => a.sort_order - b.sort_order),
    };
  }).filter((g) => g.count > 0);

  return {
    header: {
      totalRotinas: active.length,
      minutesWeek: totalMinutes,
      criticalCount: active.filter((t) => t.is_critical).length,
      indicatorLinkedCount: active.filter((t) => templateHasLinkedIndicator(t)).length,
      withoutIndicatorCount: active.filter((t) => !templateHasLinkedIndicator(t)).length,
      withoutCategoryCount: active.filter((t) => !t.category).length,
      conflictingCount: conflictingIds.size,
    },
    groups,
    highlightIds,
    conflictingIds,
  };
}

export function templateMatrixFlags(
  template: LstTemplate,
  conflictingIds: Set<string>,
): { critical: boolean; noIndicator: boolean; noCategory: boolean; conflicting: boolean } {
  return {
    critical: template.is_critical,
    noIndicator: !templateHasLinkedIndicator(template),
    noCategory: !template.category,
    conflicting: conflictingIds.has(template.id),
  };
}
